import clsx from 'clsx';
import { Formik } from 'formik';
import isEqual from 'lodash/isEqual';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalConfirm from 'src/app/components/ModalConfirm';
import { STATUS_API } from 'src/app/constant/config';
import { getListAgencyBusiness } from 'src/features/agencySlice';
import {
  maxSpeedSetting,
  updateDevice,
  updateDeviceDisplayName,
  updateLicensePlate
} from 'src/features/deviceSlice';
import { TabPanel } from './components/common';
import useCameraConfig from './hooks/useCameraConfig';
import useOilSensor from './hooks/useOilSensor';
import useSystemSettings from './hooks/useSystemSettings';
import useTransmission from './hooks/useTransmission';
import CameraConfigSection from './sections/CameraConfigSection';
import DeviceInfoSection from './sections/DeviceInfoSection';
import EditVersionFooter from './sections/EditVersionFooter';
import EditVersionHeader from './sections/EditVersionHeader';
import FuelSensorSection from './sections/FuelSensorSection';
import SystemSettingsSection from './sections/SystemSettingsSection';
import TransmissionSection from './sections/TransmissionSection';
import useStyles from './styles';
import {
  buildFuelCalibrationPoints,
  buildInitialValues,
  buildUpdateDevicePayload,
  buildValidationSchema,
  createFuelCalibrationPoint,
  getDeviceCapabilities,
  isTransmissionEnabled,
  normalizeDeviceForConfig,
  normalizeMaximumSpeed
} from './utils';

const TAB = {
  device: 0,
  fuel: 1,
  transmission: 2
};

export default function EditVersionContent({
  device,
  fetchDetail,
  onClose,
  onSaved,
  onLive4gChanged,
  onCameraConfigApplied,
  title
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formikRef = useRef();

  // 362 / 360 gửi thành công thì khi đóng modal báo nơi gọi làm mới dữ liệu
  const cameraAppliedRef = useRef(false);

  const isModal = Boolean(title && onClose);
  const closeIfModal = () => {
    if (cameraAppliedRef.current) {
      cameraAppliedRef.current = false;
      if (onCameraConfigApplied) onCameraConfigApplied();
    }
    if (onClose) onClose();
  };
  const notifySaved = () => {
    if (onSaved) onSaved();
  };

  const statusCreate = useSelector(state => state.staffSlice.statusCreate);
  const detailVehicle = useSelector(state => state.vehicleSlice.detailVehicle);
  const listAgencyBusiness = useSelector(
    state => state.agencySlice.listAgencyBusiness
  );
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isTechnician = useSelector(state => state.authSlice.isTechnician);
  const isCustomerCare = useSelector(state => state.authSlice.isCustomerCare);
  const isSale = useSelector(state => state.authSlice.isSale);
  const isLeadMarketing = useSelector(state => state.authSlice.isLeadMarketing);
  const isEndUser = useSelector(state => state.authSlice.isUser);
  const canConfigureFuel =
    isAdmin || isAgency || isTechnician || isCustomerCare || isSale || isLeadMarketing || isEndUser;

  useEffect(() => {
    if (!listAgencyBusiness || listAgencyBusiness.length === 0) {
      dispatch(getListAgencyBusiness());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const d = useMemo(() => normalizeDeviceForConfig(device), [device]);
  const {
    isCarType,
    isCameraPackageSupported,
    isMAGType,
    isOilSensorModelSupported
  } = getDeviceCapabilities(d.device_type_name);

  const [tabValue, setTabValue] = useState(TAB.device);
  const [useSuggestedName, setUseSuggestedName] = useState(d.is_display_name);
  const [allowEditLicensePlate, setAllowEditLicensePlate] = useState(false);
  const [currentLicensePlate, setCurrentLicensePlate] = useState(
    d.license_plate
  );
  const [oilType, setOilType] = useState(
    d.oil_type === false ? 'pulse' : 'percent'
  );

  const settings = useSystemSettings(d, notifySaved);
  const transmission = useTransmission(d, formikRef, notifySaved);
  const oilSensor = useOilSensor(d, notifySaved);
  const camera = useCameraConfig(d, {
    onLive4gChanged: live4g => {
      notifySaved();
      if (onLive4gChanged) onLive4gChanged(live4g);
    },
    onKeepLiveOff: () => {
      cameraAppliedRef.current = true;
    }
  });
  const { isTransmit, isCommitmentChecked } = transmission;
  // Live 4G (361): admin + CSKH thao tác, role khác chỉ xem;
  // giữ live (362): admin + CSKH + đại lý; xem cưỡng bức (360): admin + đại lý
  const canEditCamera = isCameraPackageSupported && (isAdmin || isCustomerCare);
  const canKeepLive =
    isCameraPackageSupported && (isAdmin || isCustomerCare || isAgency);
  const canForceLive = isCameraPackageSupported && (isAdmin || isAgency);
  const canApplyCamera = canKeepLive || canForceLive;

  const resetDeviceInfo = () => {
    setUseSuggestedName(d.is_display_name);
    setAllowEditLicensePlate(false);
    setCurrentLicensePlate(d.license_plate);
    setTabValue(TAB.device);
  };

  useEffect(() => {
    resetDeviceInfo();
    cameraAppliedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.id]);

  const isCommitmentRequired = tabValue === TAB.transmission && isTransmit;
  const isCameraBlockingSubmit =
    canApplyCamera && tabValue === TAB.device && camera.hasCameraConfigErrors;

  // ---- Thông tin thiết bị ----
  const handleChangeLicensePlate = licensePlate => {
    setTimeout(async () => {
      try {
        await dispatch(
          updateLicensePlate({
            id: d.id,
            data: { license_plate: licensePlate?.trim() || '' }
          })
        );

        notifySaved();
        closeIfModal();
      } catch (error) {
        console.error(error);
      }
    }, 1000);
  };

  const handleChangeSuggestedName = async name => {
    const newName = name?.trim();
    await dispatch(
      updateDeviceDisplayName({
        id: d.id,
        data: {
          name: newName,
          enable: Boolean(newName)
        }
      })
    );
    notifySaved();
    closeIfModal();
  };

  const handleToggleSuggestedName = async e => {
    const checked = e.target.checked;
    setUseSuggestedName(checked);
    if (checked) return;
    await dispatch(
      updateDeviceDisplayName({
        id: d.id,
        data: { enable: false }
      })
    );
    notifySaved();
  };

  // ---- Cảm biến xăng dầu ----
  const handleFuelSensorTypeChange = useCallback(
    newType => {
      const formik = formikRef.current;
      if (!formik) return;

      const initialOilType = d.oil_type ? 'percent' : 'pulse';

      formik.setFieldValue('fuelSensorType', newType);
      setOilType(newType);
      formik.setFieldValue(
        'fuel_calibration_points',
        newType === initialOilType
          ? buildFuelCalibrationPoints(d)
          : [createFuelCalibrationPoint()]
      );
    },
    [d]
  );

  const handleRefresh = () => {
    if (d.id) {
      fetchDetail(d.id);
    }
  };

  // ---- Lưu lại ----
  const handleSubmit = async data => {
    if (isCommitmentRequired && !isCommitmentChecked) {
      return;
    }

    // 362 / 360 phải được thiết bị xác nhận trước; thất bại thì dừng, không lưu gì thêm
    // và giữ modal mở để thử lại. Quyền xem live do 361 (switch Live 4G) quyết định,
    if (canApplyCamera && tabValue === TAB.device) {
      const { ok, appliedConfig } = await camera.applyCameraConfig();
      if (!ok) return;
      if (appliedConfig) cameraAppliedRef.current = true;
    }

    if (tabValue === TAB.device) {
      if (data.license_plate !== currentLicensePlate) {
        handleChangeLicensePlate(data.license_plate);
      }

      if (useSuggestedName) {
        handleChangeSuggestedName(data.name);
      }

      const currentMaximumSpeed = normalizeMaximumSpeed(d.maximum_speed);
      const nextMaximumSpeed = normalizeMaximumSpeed(data.maximum_speed);

      if (nextMaximumSpeed !== currentMaximumSpeed) {
        dispatch(
          maxSpeedSetting({
            license_plate: currentLicensePlate,
            data: {
              max_speed:
                nextMaximumSpeed === '' ? null : Number(nextMaximumSpeed)
            }
          })
        );
      }
    }

    const nextPayload = buildUpdateDevicePayload(data, { isTransmit });
    const originalPayload = buildUpdateDevicePayload(buildInitialValues(d), {
      isTransmit: isTransmissionEnabled(d.status_transmission)
    });
    // Chờ updateDevice trả kết quả rồi mới notifySaved (màn CSKH sẽ gọi lại /vehicles/lookup)
    if (!isEqual(nextPayload, originalPayload)) {
      await dispatch(updateDevice({ data: nextPayload, id: d.id }));
    }

    notifySaved();
    closeIfModal();
  };

  const handleClose = () => {
    resetDeviceInfo();
    settings.reset();
    transmission.reset();
    oilSensor.reset();
    closeIfModal();
  };

  return (
    <>
      <ModalConfirm {...settings.confirmDialogProps} />
      <ModalConfirm {...oilSensor.confirmDialogProps} />

      <div
        className={clsx(
          classes.dialogContent,
          isModal && classes.dialogContentModal
        )}
      >
        <EditVersionHeader
          isModal={isModal}
          title={title}
          onClose={handleClose}
          tabValue={tabValue}
          onTabChange={(event, newValue) => setTabValue(newValue)}
        />

        <Formik
          key={d.id ?? d.serial ?? 'new'}
          innerRef={formikRef}
          initialValues={buildInitialValues(d)}
          enableReinitialize={true}
          validationSchema={() =>
            buildValidationSchema({
              requireTransmitInfo: isCommitmentRequired,
              validateFuelCalibration:
                tabValue === TAB.fuel &&
                isCarType &&
                canConfigureFuel &&
                oilSensor.isEnabled,
              oilType
            })
          }
          onSubmit={handleSubmit}
        >
          {({ handleSubmit: submitForm }) => (
            <form onSubmit={submitForm} className={classes.form}>
              <div
                className={clsx(
                  classes.bodyContent,
                  isModal && classes.bodyContentModal
                )}
              >
                <TabPanel value={tabValue} index={TAB.device}>
                  <DeviceInfoSection
                    allowEditLicensePlate={allowEditLicensePlate}
                    onToggleEditLicensePlate={setAllowEditLicensePlate}
                    useSuggestedName={useSuggestedName}
                    onToggleSuggestedName={handleToggleSuggestedName}
                  />
                  <SystemSettingsSection
                    isCarType={isCarType}
                    isMAGType={isMAGType}
                    settings={settings}
                  />
                  {isCameraPackageSupported && (
                    <CameraConfigSection
                      camera={camera}
                      canEdit={canEditCamera}
                      canKeepLive={canKeepLive}
                      canForceLive={canForceLive}
                    />
                  )}
                </TabPanel>

                <TabPanel value={tabValue} index={TAB.fuel}>
                  <FuelSensorSection
                    canConfigure={canConfigureFuel}
                    isCarType={isCarType}
                    oilLevel={detailVehicle?.oil_level ?? d.oil_level}
                    onRefresh={handleRefresh}
                    onSensorTypeChange={handleFuelSensorTypeChange}
                    isSensorEnabled={oilSensor.isEnabled}
                    isSensorModelSupported={isOilSensorModelSupported}
                    sensorModel={oilSensor.sensorModel}
                    sensorToggling={oilSensor.submitting}
                    onToggleSensor={oilSensor.onToggle}
                    onChangeSensorModel={oilSensor.onChangeModel}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={TAB.transmission}>
                  <TransmissionSection
                    isCarType={isCarType}
                    isTransmit={isTransmit}
                    onToggleTransmit={transmission.onToggleTransmit}
                    isInfoTransmit={transmission.isInfoTransmit}
                    onToggleInfoTransmit={transmission.onToggleInfoTransmit}
                  />
                </TabPanel>
              </div>

              <EditVersionFooter
                isModal={isModal}
                showCommitment={isCommitmentRequired}
                isCommitmentChecked={isCommitmentChecked}
                onCommitmentChange={transmission.setIsCommitmentChecked}
                onClose={onClose ? handleClose : undefined}
                submitDisabled={
                  (isCommitmentRequired && !isCommitmentChecked) ||
                  isCameraBlockingSubmit ||
                  camera.cameraSubmitting ||
                  camera.live4gSubmitting
                }
                submitting={
                  statusCreate === STATUS_API.PENDING || camera.cameraSubmitting
                }
                submitLabel={
                  camera.cameraSubmitting ? 'Đang chờ thiết bị...' : 'Lưu lại'
                }
              />
            </form>
          )}
        </Formik>
      </div>
    </>
  );
}
